import { useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { PresetContext, type PresetContextValue } from '@/contexts/PresetContext';
import { useBusiness } from '@/hooks/useBusiness';
import { useDataStore } from '@/hooks/useDataStore';
import type { CreateInput, ID, QuotePreset, UpdateInput } from '@/types';

const NO_PRESETS: QuotePreset[] = [];

export function PresetProvider({ children }: PropsWithChildren) {
  const store = useDataStore();
  const { activeBusiness } = useBusiness();
  const businessId = activeBusiness?.id ?? null;

  const [presets, setPresets] = useState<QuotePreset[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  /** The business the bundles in state were loaded for. */
  const [loadedFor, setLoadedFor] = useState<ID | null>(null);

  useEffect(() => {
    if (!businessId) {
      setPresets([]);
      setLoadedFor(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    void store.presets.list().then((all) => {
      if (cancelled) return;
      setPresets(
        all
          .filter((preset) => preset.businessId === businessId)
          .sort((a, b) => a.displayOrder - b.displayOrder),
      );
      setLoadedFor(businessId);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [store, businessId]);

  const createPreset = useCallback<PresetContextValue['createPreset']>(
    async (input) => {
      if (!businessId) throw new Error('No active business — cannot save a preset.');
      const created = await store.presets.create({ ...input, businessId } as CreateInput<QuotePreset>);
      setPresets((current) => [...current, created].sort((a, b) => a.displayOrder - b.displayOrder));
      return created;
    },
    [store, businessId],
  );

  const updatePreset = useCallback(
    async (id: ID, patch: UpdateInput<QuotePreset>) => {
      const next = await store.presets.update(id, patch);
      setPresets((current) => current.map((preset) => (preset.id === id ? next : preset)));
    },
    [store],
  );

  const deletePreset = useCallback(
    async (id: ID) => {
      await store.presets.remove(id);
      setPresets((current) => current.filter((preset) => preset.id !== id));
    },
    [store],
  );

  // A switched-away business's bundles are never exposed while the reload runs.
  const current = loadedFor === businessId;
  const value = useMemo<PresetContextValue>(
    () => ({
      presets: current ? presets : NO_PRESETS, isLoading: isLoading || !current, createPreset, updatePreset, deletePreset,
    }),
    [presets, isLoading, current, createPreset, updatePreset, deletePreset],
  );

  return <PresetContext.Provider value={value}>{children}</PresetContext.Provider>;
}
